import { Button } from "@/components/ui/baseUi/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/baseUi/dialog"
import { Separator } from "@/components/ui/baseUi/separator"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { RefundBillIcon } from "@images/svgIcons/money/refundBillIcon"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { TransactionDetailsEntity } from "@modules/finances/entities/transactionDetailsEntity"
import { useRefundTransaction } from "@modules/finances/hooks/useFinanceActions"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { HUCardAlert } from "@modules/ui/components/huCardAlert"
import dayjs from "dayjs"
import { Info, LoaderCircle } from "lucide-react"
import { useCallback, useImperativeHandle, useState } from "react"

export type TransactionRefundModalRef = {
  open: () => void
} & React.ComponentProps<"div">

type Props = {
  transactionDetails: TransactionDetailsEntity
  ref: React.Ref<TransactionRefundModalRef>
  className?: string
}

export function TransactionRefundModal({ transactionDetails, ref, className, ...props }: Props) {
  const [showModal, setShowModal] = useState(false)

  const language = useLanguage()
  const { refundTransaction, isPending: isPendingRefundTransaction } = useRefundTransaction()

  const linesLenght = transactionDetails.lines.length ?? 0

  const onHide = useCallback(() => {
    setShowModal(false)
  }, [])

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  const handleRefund = () => {
    if (!transactionDetails.refundable) return
    refundTransaction(transactionDetails.transactionId).then(() => {
      setShowModal(false)
    })
  }

  return (
    <Dialog open={showModal} onOpenChange={onHide}>
      <DialogContent className={cn("max-w-md", className)} {...props}>
        <DialogHeader className="flex flex-col items-center gap-2 text-center">
          <DialogTitle>
            <RefundBillIcon color="white" />
          </DialogTitle>
          <DialogDescription>
            <Text fontStyle="TL" className="text-textDefault-heading">
              {word("box.transaction.refund.modal.alert.title")}
            </Text>
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-6">
          {/* ALERTE */}
          <HUCardAlert
            title={word("box.transaction.refund.modal.alert.content")}
            icon={<Info size={35} className="mx-2 shrink-0" />}
            titleFontStyle="BS"
          />

          <div className="flex flex-col gap-2">
            <Text fontStyle="TS" className="flex items-center justify-center text-center text-textDefault-heading">
              {word("box.transaction.refund.modal.header.content")}
            </Text>
            {transactionDetails.lines.map((invoice, index) => (
              <div key={index} className="flex flex-col gap-2">
                <div className="flex gap-2 overflow-hidden px-2">
                  <div className="flex w-3/4 grow flex-col overflow-hidden">
                    <Text fontStyle="BM" className="flex overflow-hidden overflow-ellipsis text-textDefault-heading">
                      {`${invoice.description}`}
                    </Text>
                    {invoice.periodEnd && invoice.periodStart ? (
                      <Text fontStyle="BS" className="flex text-neutral-400">
                        {`${dayjs(invoice.periodStart).format("L")} - ${dayjs(invoice.periodEnd).format("L")}`}
                      </Text>
                    ) : null}
                  </div>
                  <div className="flex w-1/4 grow flex-col">
                    <Text fontStyle="BM" className="flex text-textDefault-heading">
                      {`${formatCurrency(transactionDetails.amount ?? 0, transactionDetails.currency, language)}`}
                    </Text>
                  </div>
                </div>

                {index < linesLenght - 1 ? <Separator orientation="horizontal" className="flex-1" /> : null}
              </div>
            ))}
          </div>
        </div>
        {/* BUTTON */}
        <DialogFooter className="mb-3 flex flex-row gap-2">
          <Button
            variant="outline"
            onClick={(event) => {
              event.preventDefault()
              setShowModal(false)
            }}
            className="flex-1"
          >
            {word("global.cancel")}
          </Button>
          <Button
            isLoading={isPendingRefundTransaction}
            variant="primary"
            onClick={handleRefund}
            disabled={isPendingRefundTransaction}
            className="flex-1"
          >
            {isPendingRefundTransaction && <LoaderCircle className="animate-spin" />}
            {word("box.transaction.refund.modal.alert.action")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
